/* eslint-disable no-magic-numbers */

import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import mapApi from '../../api/map';
import * as types from '../mutation-types';

import getUuid from '../../helpers/get-uuid';
import getProjectsWithPictures, { parseProjectPaymentMethods } from '../../helpers/map-helpers';

const alarmEntity = new schema.Entity('alarms');
const alarmSectionEntity = new schema.Entity('alarmSections', {
  alarms: [alarmEntity],
});
const checkEntity = new schema.Entity('checks');
const checklistEntity = new schema.Entity('checklists', {
  checks: [checkEntity],
});
const projectEntity = new schema.Entity('projects', {
  checklists: [checklistEntity],
  alarmSections: [alarmSectionEntity],
});

const initialState = {
  plotCart: {},
  purchase: {},
  picture: {},
  foundation: null,
  projectGroups: [],
  projects: {},
  alarmSections: {},
  alarms: {},
  checklists: {},
  checks: {},
  areas: {},
  activeProjectId: null,
  faces: [],
  limit: null,
  bounds: null,
  currentZoom: null,
  focusedProjectId: null,
  currentAreaId: null,
  creatingPurchase: false,
  mapCenter: null,
  initialProject: null,
  whitelabeledProject: null,
  whitelabeledFoundation: null,
};

const actions = {
  initMapProjects({ commit, dispatch, getters, state }, { projectType }) {
    const filters = { includeAreas: true, projectType };

    if (state.whitelabeledProject) {
      return mapApi.getProject({ projectSlug: state.whitelabeledProject, includeAreas: true })
        .then(project => {
          commit(types.RESET_AREAS);
          commit(types.SET_AREAS, project.areas);

          const normalizedProjects = normalize(project, projectEntity);
          normalizedProjects.entities.projects = getProjectsWithPictures(normalizedProjects.entities.projects);
          commit(types.SET_PROJECTS_WITH_AREAS, normalizedProjects);
          commit(types.SET_ACTIVE_PROJECT_ID, getters.projectsAsArray[0].id);
        });
    }

    if (state.whitelabeledFoundation) {
      dispatch('setFoundation');
      dispatch('setProjectGroups');
      filters.foundationSlug = state.whitelabeledFoundation;
    }

    return mapApi.getProjects(filters).then(projects => {
      commit(types.RESET_AREAS);
      projects.forEach(project => {
        commit(types.SET_AREAS, project.areas);
      });

      const normalizedProjects = normalize({ projects }, { projects: [projectEntity] });
      normalizedProjects.entities.projects = getProjectsWithPictures(normalizedProjects.entities.projects);
      commit(types.SET_PROJECTS_WITH_AREAS, normalizedProjects);
      commit(types.SET_ACTIVE_PROJECT_ID, getters.projectsAsArray[0].id);
    });
  },

  getFaces({ commit, state }) {
    mapApi.getFaces(state.bounds, state.limit, state.whitelabeledProject, state.whitelabeledFoundation).then(faces => {
      commit(types.SET_FACES, faces);
    });
  },
  selectPic(
    { dispatch, state },
    { offset, bounds, projectId, areaId, sideLength }
  ) {
    const nwPoint = bounds.getNorthWest();

    // change SW local offset to NW offset
    const offsets = {
      latOffset: offset.latOffset + sideLength,
      lngOffset: offset.lngOffset,
    };

    const geoPoint = [nwPoint.lat, nwPoint.lng];

    const plot = {
      geoPoint,
      areaId,
      nwOffsetPoint: { lat: offsets.latOffset, lon: offsets.lngOffset },
      sideLength,
    };

    if (state.purchase && state.purchase.id) {
      plot.purchaseId = state.purchase.id;
    } else if (!state.creatingPurchase) {
      dispatch('startPurchase', projectId);
    }
    dispatch('addPlotToCart', plot);
  },
  selectRandomPic({ dispatch }, { areaId, sideLength }) {
    return dispatch('addPlotToCart', { areaId, sideLength });
  },
  addPlotToCart({ commit, dispatch }, plot) {
    plot.id = getUuid();
    commit(types.ADD_TO_PLOTCART, plot);

    if (plot.purchaseId) {
      return dispatch('addPlotToPurchase', plot);
    }

    return null;
  },
  addPlotsToPurchase({ dispatch, getters }, purchaseId) {
    const allPlots = [];
    getters.plotCartAsArray.forEach(plot => {
      plot.purchaseId = purchaseId;
      allPlots.push(dispatch('addPlotToPurchase', plot));
    });

    return Promise.all(allPlots);
  },
  addPlotToPurchase({ commit }, plot) {
    return mapApi
      .addToPurchase(plot)
      .then(response => {
        commit(types.REMOVE_FROM_PLOTCART, plot);
        commit(types.ADD_TO_PLOTCART, response.plot);
      })
      .catch(err => {
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again.`);
        throw new Error(err);
      });
  },
  removePlotFromCart({ commit, dispatch, state }, plotId) {
    const plot = state.plotCart[plotId];
    if (plot.purchaseId) {
      dispatch('removePlotFromPurchase', plotId);
    }
    commit(types.REMOVE_FROM_PLOTCART, plot);
  },
  removePlotFromPurchase({ commit, state }, plotId) {
    const plot = state.plotCart[plotId];

    return mapApi.removeFromPurchase(plot, state.purchase.id).then(
      () => {},
      err => {
        commit(types.ADD_TO_PLOTCART, plot);
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again`);
      }
    );
  },
  updatePlotInCart({ commit, dispatch, state }, plot) {
    const previousPlotState = state.plotCart[plot.id];
    const newPlot = Object.assign({}, previousPlotState, plot);
    commit(types.UPDATE_PLOT, newPlot);
    if (newPlot.purchaseId) {
      dispatch('updatePlotInPurchase', { plot, previousPlotState });
    }
  },
  updatePlotInPurchase({ commit, state }, { plot, previousPlotState }) {
    return mapApi.updatePlot(plot, state.purchase.id).then(
      response => {
        commit(types.UPDATE_PLOT, response.plot);
      },
      err => {
        commit(types.UPDATE_PLOT, previousPlotState);
        commit(types.SHOW_TOAST, `[ERROR] Error updating plot, please try again. ${err.message}`);
      }
    );
  },
  startPurchase({ commit, dispatch, getters }, projectId) {
    commit(types.CREATING_PURCHASE, true);
    const mapPath = `/${getters.entityType}/${getters.entitySlug}`;

    return mapApi
      .purchaseStart(projectId, mapPath)
      .then(response => {
        commit(types.CREATING_PURCHASE, false);
        commit(types.SAVE_PURCHASE, {
          apiToken: response.purchase.apiToken,
          purchaseId: response.purchase.id,
          purchaseStatus: response.purchase.status,
        });

        return dispatch('addPlotsToPurchase', response.purchase.id);
      })
      .catch(err => {
        commit(types.CREATING_PURCHASE, false);
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again.`);
        throw new Error(err);
      });
  },
  cancelPurchase({ state, commit, rootState }) {
    const purchaseId = state.purchase.id;
    const purchaseToken = rootState.auth.apiToken;

    commit(types.CLEAR_CART);
    commit(types.RESET_PURCHASE);

    return mapApi.cancelPurchase(purchaseId, purchaseToken);
  },
  updatePurchase({ commit, state }, { id, token }) {
    const purchaseId = id || state.purchase.id;
    if (!id || !token) return false;
    if (token) {
      commit(types.SAVE_PURCHASE, { apiToken: token, purchaseId });
    }

    return mapApi.getPurchase(id).then(
      response => {
        commit(types.UPDATE_PURCHASE, response.purchase);
        if (state.activeProjectId !== response.purchase.projectId) {
          commit(types.SET_ACTIVE_PROJECT_ID, response.purchase.projectId);
        }
        if (response.purchase.plots && response.purchase.plots.length > 0 &&
          (!response.purchase.name || !response.purchase.email || !response.purchase.hasPhoto)) {
          response.purchase.plots.forEach(plot => {
            commit(types.ADD_TO_PLOTCART, plot);
          });
        }
        if (response.purchase.payments.length > 0 &&
            response.purchase.payments[response.purchase.payments.length - 1].paymentState === 'canceled') {
          commit(types.CLEAR_CART);
          commit(types.RESET_PURCHASE);
        }
        if (response.purchase.status === 'paid') {
          commit(types.TOGGLE_PURCHASE_SIDEBAR, true);
        }
      },
      err => {
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again.`);
      }
    );
  },
  checkoutPurchase({ commit, state, rootState }) {
    return mapApi.checkoutPurchase(state.purchase.id, rootState.auth.couponCode).then(
      response => {
        commit(types.UPDATE_PURCHASE, response.purchase);
      },
      err => {
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again`);
      }
    );
  },
  updatePicInformation({ commit, dispatch, state }, form) {
    return mapApi
      .savePicInformation(state.purchase.id, form, state.purchase.projectId)
      .then(response => {
        commit(types.UPDATE_PURCHASE, response.purchase);
        dispatch('getFaces');
      })
      .catch(err => {
        commit(types.SHOW_TOAST, `[ERROR] ${err.message}. Please try again.`);
        throw new Error(err);
      });
  },
  focusOnProject({ commit }, projectId) {
    commit(types.FOCUS_ON_PROJECT, projectId);
  },
  setFoundation({ commit, state }) {
    mapApi.getFoundation(state.whitelabeledFoundation).then(response => commit(types.SET_FOUNDATION, response));
  },
  setProjectGroups({ commit, state }) {
    mapApi.getProjectGroups(state.whitelabeledFoundation).then(response => commit(types.SET_PROJECT_GROUPS, response));
  },
  toggleProjectGroup({ commit }, groupId) {
    commit(types.TOGGLE_GROUP, groupId);
  },
  createAlarm({ commit }, { alarmSectionId, date }) {
    mapApi.createAlarm(alarmSectionId, date)
      .then(response => commit(types.CREATE_ALARM, { ...response, alarmSectionId }));
  },
  destroyAlarm({ commit }, { alarmSectionId, alarmId }) {
    mapApi.destroyAlarm(alarmId)
      .then(response => commit(types.DESTROY_ALARM, { ...response, alarmSectionId, alarmId }));
  },
  updateCheck({ commit }, { checkId, done }) {
    return mapApi.updateCheck(checkId, done)
      .then(response => commit(types.UPDATE_CHECK, { ...response }));
  },
};

const mutations = {
  [types.SET_PROJECTS_WITH_AREAS](state, payload) {
    Object.keys(payload.entities).forEach(entityType => {
      state[entityType] = payload.entities[entityType];
    });
  },
  [types.SET_AREAS](state, payload) {
    const areasById = {};
    payload.forEach(area => {
      areasById[area.id] = area;
    });
    state.areas = Object.assign({}, state.areas, areasById);
  },
  [types.RESET_AREAS](state) {
    state.areas = {};
  },
  [types.SET_ACTIVE_PROJECT_ID](state, payload) {
    state.activeProjectId = payload;
  },
  [types.SET_CURRENT_AREA_ID](state, payload) {
    state.currentAreaId = payload;
  },
  [types.SET_FACES](state, payload) {
    state.faces = payload;
  },
  [types.SET_LIMIT](state, payload) {
    state.limit = payload;
  },
  [types.SET_BOUNDS](state, payload) {
    state.bounds = _.cloneDeep(payload);
  },
  [types.SET_CURRENT_ZOOM](state, payload) {
    state.currentZoom = payload;
  },
  [types.SAVE_PURCHASE](state, payload) {
    state.purchase = {
      id: payload.purchaseId,
      status: payload.purchaseStatus,
    };
  },
  [types.RESET_PURCHASE](state) {
    state.purchase = {};
  },
  [types.ADD_TO_PLOTCART](state, payload) {
    state.plotCart = Object.assign({}, state.plotCart, { [payload.id]: payload });
  },
  [types.REMOVE_FROM_PLOTCART](state, payload) {
    const plotCart = Object.assign({}, state.plotCart);
    delete plotCart[payload.id];
    state.plotCart = plotCart;
  },
  [types.MASS_ASSIGN_PLOTCART](state, payload) {
    state.plotCart = Object.assign({}, state.plotCart, payload);
  },
  [types.UPDATE_PURCHASE](state, payload) {
    state.purchase = Object.assign({}, state.purchase, payload);
  },
  [types.UPDATE_PLOT](state, payload) {
    state.plotCart = Object.assign({}, state.plotCart, { [payload.id]: payload });
  },
  [types.CLEAR_CART](state) {
    state.plotCart = {};
  },
  [types.FOCUS_ON_PROJECT](state, payload) {
    state.focusedProjectId = payload;
  },
  [types.FOCUS_ON_PROJECT_DONE](state) {
    state.focusedProjectId = null;
  },
  [types.CREATING_PURCHASE](state, payload) {
    state.creatingPurchase = payload;
  },
  [types.SET_MAP_CENTER](state, payload) {
    state.mapCenter = payload;
  },
  [types.SET_INITIAL_PROJECT](state, payload) {
    state.initialProject = payload;
  },
  [types.SET_WHITELABELED_PROJECT](state, payload) {
    state.whitelabeledProject = payload;
  },
  [types.SET_WHITELABELED_FOUNDATION](state, payload) {
    state.whitelabeledFoundation = payload;
  },
  [types.SET_FOUNDATION](state, payload) {
    state.foundation = payload;
  },
  [types.SET_PROJECT_GROUPS](state, payload) {
    state.projectGroups = payload.map((group) => {
      group.open = false;

      return group;
    });
  },
  [types.TOGGLE_GROUP](state, payload) {
    const projectGroupsCopy = state.projectGroups.map((group) => {
      group.open = group.id === payload ? !group.open : false;

      return group;
    });
    state.projectGroups = projectGroupsCopy;
  },
  [types.CREATE_ALARM](state, payload) {
    state.alarmSections[payload.alarmSectionId].alarms.push(payload.alarm.id);
    state.alarms = { ...state.alarms, [payload.alarm.id]: payload.alarm };
  },
  [types.DESTROY_ALARM](state, payload) {
    const alarmSectionCopy = state.alarmSections[payload.alarmSectionId];
    _.remove(alarmSectionCopy.alarms, (alarmId) => alarmId === payload.alarmId);
    state.alarmSections = { ...state.alarmSections, [payload.alarmSectionId]: alarmSectionCopy };

    const alarmsCopy = { ...state.alarms };
    delete alarmsCopy[payload.alarmId];
    state.alarms = { ...alarmsCopy };
  },
  [types.UPDATE_CHECK](state, payload) {
    state.checks = { ...state.checks, [payload.check.id]: payload.check };
  },
};

const getters = {
  projectsAsArray: state => {
    const projects = _.values(state.projects).sort((a, b) => a.index - b.index);

    return projects;
  },
  projectsWithActivePicAreasAsArray: state => {
    const projects = _.values(state.projects)
      .filter((project) => project.hasActivePicAreas)
      .sort((a, b) => a.index - b.index);

    return projects;
  },
  projectGroupsWithProjects: (state, otherGetters) => {
    const groupsWithProjects = state.projectGroups.map((group) => {
      const groupCopy = { ...group };
      groupCopy.projects = otherGetters.projectsAsArray.filter((project) => project.projectGroupId === group.id);

      return groupCopy;
    });

    return groupsWithProjects;
  },
  projectsNotInAGroupAsArray: (state, otherGetters) => {
    if (!state.foundation) return otherGetters.projectsAsArray;

    const groupIds = state.projectGroups.map((group) => group.id);

    return otherGetters.projectsAsArray.filter((project) => !groupIds.includes(project.projectGroupId));
  },
  activeProject: state => {
    const activeProject = state.projects[state.activeProjectId] ?
      JSON.parse(JSON.stringify(state.projects[state.activeProjectId])) :
      null;
    if (activeProject && activeProject.projectSections) {
      activeProject.projectSections.sort((a, b) => a.index - b.index);
      activeProject.description = activeProject.projectSections.find(
        projectSection => projectSection.sectionType === 'general_description'
      );
    }

    return activeProject;
  },
  activeProjectGroup: (state, otherGetters) =>
    (otherGetters.activeProject ?
      state.projectGroups.find((group) => group.id === otherGetters.activeProject.projectGroupId) :
      null),
  paymentMethods: (state, otherGetters) => parseProjectPaymentMethods(otherGetters.activeProject),
  currentArea: state => state.areas[state.currentAreaId],
  plotCartAsArray: state => _.values(state.plotCart),
  plotCartSize: state =>
    _.values(state.plotCart).reduce((prev, plot) => prev + plot.sideLength * plot.sideLength, 0),
  focusedProject: state => state.projects[state.focusedProjectId],
  projectFromSlug: (state) => (slug) => {
    const projects = _.values(state.projects).filter(project => project.slug === slug);

    return projects[0];
  },
  entityType: state => {
    if (state.whitelabeledFoundation) {
      return 'f';
    } else if (state.whitelabeledProject) {
      return 'p';
    }

    return '';
  },
  entitySlug: state => {
    if (state.whitelabeledFoundation) {
      return state.whitelabeledFoundation;
    } else if (state.whitelabeledProject) {
      return state.whitelabeledProject;
    }

    return '';
  },
  projectChecklists: (state) => (projectId) =>
    state.projects[projectId] &&
      state.projects[projectId].checklists.map(checklistId => state.checklists[checklistId]),
  activeProjectChecklists: (state, otherGetters) =>
    otherGetters.projectChecklists(state.activeProjectId),
  checklistChecks: (state) => (checklistId) =>
    state.checklists[checklistId] && state.checklists[checklistId].checks.map(checkId => state.checks[checkId]),
  projectAlarmSections: (state) => (projectId) =>
    state.projects[projectId] &&
      state.projects[projectId].alarmSections.map(alarmSectionId => state.alarmSections[alarmSectionId]),
  activeProjectAlarmSections: (state, otherGetters) =>
    otherGetters.projectAlarmSections(state.activeProjectId),
  alarmSectionAlarms: (state) => (alarmSectionId) =>
    state.alarmSections[alarmSectionId] &&
      state.alarmSections[alarmSectionId].alarms.map(alarmId => state.alarms[alarmId]),
};

export default {
  state: initialState,
  mutations,
  actions,
  getters,
};
